import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import i18n from './i18n'
import { isPlatformMediaUrl } from './images'

export function getRoleBadgeStyles(role: 'owner' | 'admin' | 'moderator' | 'member') {
  switch (role) {
    case 'owner':
      return 'bg-yellow-100 text-yellow-700 border border-yellow-200'
    case 'admin':
      return 'bg-purple-100 text-purple-700 border border-purple-200'
    case 'moderator':
      return 'bg-blue-100 text-blue-700 border border-blue-200'
    default:
      return 'bg-gray-100 text-gray-700 border border-gray-200'
  }
}

export function getRoleLabel(role: string) {
  switch (role) {
    case 'owner':
      return 'Owner'
    case 'admin':
      return 'Administrator'
    case 'moderator':
      return 'Moderator'
    default:
      return 'Member'
  }
}

interface ShareOptions {
  title?: string
  text?: string
  url?: string
}

export async function shareContent(options: ShareOptions) {
  const shareData = {
    title: options.title || document.title,
    text: options.text,
    url: options.url || window.location.href
  }

  try {
    // Only try navigator.share if it exists and we have the required data
    if (navigator.share && shareData.url) {
      try {
        await navigator.share(shareData)
        return { success: true, method: 'share' }
      } catch {
        // If share fails, fall back to clipboard
        console.log('Native share failed, falling back to clipboard')
      }
    }
    
    // Fallback to clipboard
    await navigator.clipboard.writeText(shareData.url || '')
    return { success: true, method: 'clipboard' }
  } catch (error) {
    console.error('Error sharing:', error)
    return { success: false, error }
  }
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** For `.filter(isTruthy)` on a list built with `cond && item`: drops the falsy entries and narrows the type. */
export const isTruthy = <T,>(value: T): value is Exclude<T, false | '' | 0 | null | undefined> => Boolean(value)

/* Paginated SDK listings come back as { items, pagination }; a few endpoints
   still answer with a bare array. Anything else (null, an error body) becomes
   an empty list, so a caller can always .filter/.map the result. */
export function listItems<T = unknown>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[]
  const items = (result as { items?: unknown } | null | undefined)?.items
  return Array.isArray(items) ? (items as T[]) : []
}

// Safe HTML sanitization function
export function sanitizeHTML(html: string): string {
  /* Parse into an inert document. Assigning to `innerHTML` of an element
     created in the live document starts loading `<img src>` right away, so an
     `onerror` handler ran before the attributes below were ever stripped. A
     DOMParser document has no browsing context: nothing loads, nothing runs. */
  const temp = new DOMParser().parseFromString(html, 'text/html').body
  
  // Allowed tags and attributes
  const allowedTags = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'pre', 'img']
  /* No `srcset`, `style` or `data-*` on images: only the one `src` checked below. */
  const allowedAttributes: Record<string, string[]> = {
    'a': ['href', 'target', 'rel'],
    'img': ['src', 'alt', 'width', 'height'],
    '*': ['class'] // Allow class on all elements
  }
  
  // Function to sanitize a single element
  const sanitizeElement = (element: Element): void => {
    const tagName = element.tagName.toLowerCase()
    
    // Remove disallowed tags
    if (!allowedTags.includes(tagName)) {
      element.remove()
      return
    }
    
    // Remove disallowed attributes
    const allowedAttrs = [
      ...(allowedAttributes[tagName] || []),
      ...(allowedAttributes['*'] || [])
    ]
    
    Array.from(element.attributes).forEach(attr => {
      if (!allowedAttrs.includes(attr.name)) {
        element.removeAttribute(attr.name)
      }
    })
    
    // Sanitize href attributes to prevent javascript: and data: URLs
    if (tagName === 'a') {
      const href = element.getAttribute('href')
      /* Browsers ignore case, whitespace and control characters in the scheme
         ("JavaScript:", " javascript:", "java\tscript:"), so compare without them. */
      // eslint-disable-next-line no-control-regex
      const scheme = (href || '').replace(/[\u0000-\u0020]/g, '').toLowerCase()
      if (href && (scheme.startsWith('javascript:') || scheme.startsWith('data:') || scheme.startsWith('vbscript:'))) {
        element.removeAttribute('href')
      }
      /* External links open in a new tab. A protocol-relative `//host` is
         external too, and any link that ends up with `target="_blank"`
         (including one the author set on a relative href) gets `rel`. */
      const current = element.getAttribute('href')
      // eslint-disable-next-line no-control-regex
      const normalized = (current || '').replace(/[\u0000-\u0020]/g, '').toLowerCase()
      if (normalized.startsWith('http://') || normalized.startsWith('https://') || normalized.startsWith('//')) {
        /* A full address on the app's own host (a post or space of this
           community) is internal: it opens in the same tab. */
        let sameHost = false
        try {
          sameHost = typeof window !== 'undefined' && new URL(current || '', window.location.href).host === window.location.host
        } catch {
          sameHost = false
        }
        if (sameHost) {
          if (element.getAttribute('target')?.toLowerCase() === '_blank') element.removeAttribute('target')
        } else {
          element.setAttribute('target', '_blank')
        }
      }
      if (element.getAttribute('target')?.toLowerCase() === '_blank') {
        element.setAttribute('rel', 'noopener noreferrer')
      }
    }

    /* Images only from our own media host (https), where imported and
       Substack posts have theirs re-hosted. Anything else (another host,
       `data:`, `http:`, a relative path) is dropped, not left as a broken
       image that would make readers' browsers call a third party. */
    if (tagName === 'img') {
      if (!isPlatformMediaUrl(element.getAttribute('src')?.trim())) {
        element.remove()
        return
      }
      element.setAttribute('loading', 'lazy')
      element.setAttribute('decoding', 'async')
      element.setAttribute('class', 'h-auto max-w-full rounded-lg')
    }

    // Remove event handlers and script-like attributes
    Array.from(element.attributes).forEach(attr => {
      if (attr.name.startsWith('on') || attr.name.includes('script')) {
        element.removeAttribute(attr.name)
      }
    })
  }
  
  // Recursively sanitize all elements
  const sanitizeNode = (node: Node): void => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element
      sanitizeElement(element)
      
      // Sanitize children
      Array.from(element.childNodes).forEach(child => {
        sanitizeNode(child)
      })
    }
  }
  
  // Remove script tags completely
  temp.querySelectorAll('script').forEach(script => script.remove())
  
  // Remove style tags (inline styles are handled by attribute filtering)
  temp.querySelectorAll('style').forEach(style => style.remove())
  
  // Sanitize all nodes
  Array.from(temp.childNodes).forEach(child => {
    sanitizeNode(child)
  })
  
  return temp.innerHTML
}

export function formatDistanceToNow(date: Date): string {
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) {
    return i18n.t('common:time.now')
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return i18n.t('common:time.minuteAgo', { count: diffInMinutes })
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return i18n.t('common:time.hourAgo', { count: diffInHours })
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) {
    return i18n.t('common:time.dayAgo', { count: diffInDays })
  }

  const diffInWeeks = Math.floor(diffInDays / 7)
  if (diffInWeeks < 4) {
    return i18n.t('common:time.weekAgo', { count: diffInWeeks })
  }

  const diffInMonths = Math.floor(diffInDays / 30)
  if (diffInMonths < 12) {
    return i18n.t('common:time.monthAgo', { count: diffInMonths })
  }

  const diffInYears = Math.floor(diffInDays / 365)
  return i18n.t('common:time.yearAgo', { count: diffInYears })
}

export function formatDateTime(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${day}/${month}/${year} às ${hours}:${minutes}`
}